import React, { cloneElement } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { Icon, palettes, l, tints } from '@manychat/manyui'

import config from 'common/builder/components/chart/FlowChart/config'
import { Config } from 'common/builder/components/chart/FlowChart/nodes/shared/NodeCard/NodeCard'

export const DEFAULT_NODE_WIDTH = 520
export const DEFAULT_ICON_RENDER_SIZE = 128

const PlaceholderAddMessage = l.makeTranslate('Click to add a message')
const PlaceholderAddAssistant = l.makeTranslate('Define instructions for AI Step')
const PlaceholderAddAction = l.makeTranslate('Click to add an action')
const PlaceholderAddCondition = l.makeTranslate('Click to add a condition')
const PlaceholderAddVariant = l.makeTranslate('Click to add a variant')
const PlaceholderSelectFlow = l.makeTranslate('Click to Select Automation')
const PlaceholderAddTextInSideBar = l.makeTranslate('Click to add text in the sidebar')

const defaultConfig = {
  width: 420,
  horizontalPadding: 28,
  borderColor: '#C1C9D6',
  borderColorHover: '#0084FF',
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: undefined,
  icon: null,
  iconComponent: Icon.OrphanAction,
}

const createNodeConfig = (config: Partial<Config>) => ({
  ...defaultConfig,
  ...config,
})

export const ActionConfig = createNodeConfig({
  width: 380,
  headerBackground: '#FFECA7',
  highlightColor: '#FFB340',
  placeholderBackground: '#FFFBED',
  placeholderBorderColor: '#FFB340',
  placeholderText: PlaceholderAddAction,
  icon: renderToStaticMarkup(
    cloneElement(<Icon.Rules color={tints.orange[3]} size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.Rules,
  iconColor: tints.orange[3],
})

export const SmartDelayConfig = createNodeConfig({
  width: 420,
  headerBackground: '#FFDBD6',
  highlightColor: '#EB5038',
  placeholderBackground: '#FFF6F4',
  placeholderBorderColor: '#F07C6A',
  icon: renderToStaticMarkup(
    cloneElement(<Icon.Time color={palettes.semantic.danger} size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.Time,
  iconColor: palettes.semantic.danger,
})

export const ConditionConfig = createNodeConfig({
  width: 420,
  headerBackground: '#B2FCF4',
  highlightColor: '#69D9F5',
  placeholderBackground: '#EAFFFD',
  placeholderBorderColor: '#79DDF7',
  placeholderText: PlaceholderAddCondition,
  icon: renderToStaticMarkup(
    cloneElement(<Icon.Condition color={tints.lightBlue[3]} size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.Condition,
  iconColor: tints.lightBlue[3],
})

export const SplitConfig = createNodeConfig({
  width: 380,
  headerBackground: '#E0D7FF',
  highlightColor: '#8B55F7',
  placeholderBackground: '#FFF7FF',
  placeholderBorderColor: '#A47BF7',
  placeholderText: PlaceholderAddVariant,
  icon: renderToStaticMarkup(
    cloneElement(<Icon.OrphanSplit color={tints.purple[3]} size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.OrphanSplit,
  iconColor: tints.purple[3],
})

export const GotoConfig = createNodeConfig({
  width: 420,
  headerBackground: '#E3FC9B',
  highlightColor: '#92BF0A',
  placeholderBackground: '#F7FFDF',
  placeholderBorderColor: '#A6CC33',
  placeholderText: PlaceholderSelectFlow,
  icon: renderToStaticMarkup(
    cloneElement(<Icon.GoToFlow size={DEFAULT_ICON_RENDER_SIZE} color="#87B400" />),
  ),
  iconComponent: Icon.GoToFlow,
  iconColor: '#87B400',
})

export const MessengerConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'Facebook',
  icon: renderToStaticMarkup(
    cloneElement(<Icon.MessengerChannel size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.MessengerChannel,
})

const TriggerHeaderBackground = '#BBF2FF' //'#CCFDE3'

export const KeywordConfig = createNodeConfig({
  width: 440,
  headerBackground: TriggerHeaderBackground,
})

export const WidgetConfig = createNodeConfig({
  width: 440,
  headerBackground: TriggerHeaderBackground,
})

export const RuleConfig = createNodeConfig({
  width: 440,
  headerBackground: TriggerHeaderBackground,
})

export const ExternalTriggerConfig = createNodeConfig({
  width: 440,
  headerBackground: TriggerHeaderBackground,
})

export const SmsConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'SMS',
  icon: renderToStaticMarkup(cloneElement(<Icon.SmsChannel size={DEFAULT_ICON_RENDER_SIZE} />)),
  iconComponent: Icon.SmsChannel,
})

export const EmailConfig = createNodeConfig({
  width: 580,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'Email',
  icon: renderToStaticMarkup(cloneElement(<Icon.EmailChannel size={DEFAULT_ICON_RENDER_SIZE} />)),
})

export const EmailNewConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'Email',
  icon: renderToStaticMarkup(cloneElement(<Icon.EmailChannel size={DEFAULT_ICON_RENDER_SIZE} />)),
  iconComponent: Icon.EmailChannel,
})

export const CommentSmallConfig = createNodeConfig({
  width: 320,
  placeholderText: PlaceholderAddTextInSideBar,
  placeholderTextColor: '#CAAB7C',
})

export const CommentMediumConfig = createNodeConfig({
  ...CommentSmallConfig,
  width: DEFAULT_NODE_WIDTH,
})

export const CommentLargeConfig = createNodeConfig({
  ...CommentSmallConfig,
  width: 920,
})

export const StartingStepConfig = createNodeConfig({
  highlightColor: '#0BCB6B',
  isLargeHeaderIcon: true,
  width: config.STARTING_STEP_NODE_WIDTH,
  icon: renderToStaticMarkup(cloneElement(<Icon.OrphanAction size={96} />)),
  iconComponent: Icon.OrphanAction,
})

export const ChooseFirstStepNodeConfig = createNodeConfig({
  width: 350,
  isLargeHeaderIcon: true,
})

export const WhatsAppConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'WhatsApp',
  icon: renderToStaticMarkup(
    cloneElement(<Icon.WhatsAppChannel size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  rightIcon: renderToStaticMarkup(
    cloneElement(<Icon.MessageTemplates size={80} color="#0BCB6B" />),
  ),
  iconComponent: Icon.WhatsAppChannel,
})

export const InstagramConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'Instagram',
  icon: renderToStaticMarkup(
    cloneElement(<Icon.InstagramChannel size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.InstagramChannel,
})

export const TikTokConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'TikTok',
  icon: renderToStaticMarkup(cloneElement(<Icon.TikTokChannel size={DEFAULT_ICON_RENDER_SIZE} />)),
  iconComponent: Icon.TikTokChannel,
})

export const TelegramConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBackground: '#EFF7FF',
  placeholderBorderColor: '#0084FF',
  placeholderText: PlaceholderAddMessage,
  preheader: 'Telegram',
  icon: renderToStaticMarkup(
    cloneElement(<Icon.TelegramChannel size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.TelegramChannel,
})

export const AiAgentConfig = createNodeConfig({
  width: DEFAULT_NODE_WIDTH,
  headerBackground: '#FDFDFD',
  placeholderBorderColor: tints.neutral[4],
  placeholderBackground: tints.neutral[6],
  placeholderText: PlaceholderAddAssistant,
  icon: renderToStaticMarkup(
    cloneElement(<Icon.AiStep color={palettes.typography.body} size={DEFAULT_ICON_RENDER_SIZE} />),
  ),
  iconComponent: Icon.AiStep,
})
